import { SAUDI_BRAND } from '../../products/saudi/config/saudiBrandConfig';
import { getBrandDefaultCurrency, getBrandVatRate } from '../../shared/utils/brandCurrency';

/** Canonical defaults — Arabic-first, aligned with ventes submodules. */
export const DEFAULT_VENTES_CONFIG = {
  // الأسعار والخصومات
  enablePriceRules: true,
  priceRuleReferencePrefix: 'PRM',
  autoGeneratePriceRuleReference: true,
  allowStackableDiscounts: false,
  defaultDiscountType: 'percentage',
  requirePromoCodeApproval: false,
  defaultPriceIncludesVat: false,

  // أوامر العملاء
  enableOrders: true,
  orderReferencePrefix: 'CMD',
  autoGenerateOrderReference: true,
  requireCustomer: true,
  allowPartialDelivery: true,
  defaultDeliveryLeadTimeDays: 5,
  defaultPaymentTerms: '30 يوم من تاريخ الفاتورة',

  // دفتر المبيعات
  enableSalesLedger: true,
  saleReferencePrefix: 'VENT',
  autoGenerateSaleReference: true,
  autoConvertDeliveredOrders: false,
  defaultPaymentStatus: 'pending',

  // التسليمات
  enableDeliveries: true,
  deliveryReferencePrefix: 'LIV',
  autoGenerateDeliveryReference: true,
  signatureRequired: true,
  trackDeliveries: true,

  // مرتجعات المبيعات
  enableReturns: true,
  returnReferencePrefix: 'SR',
  autoGenerateReturnReference: true,
  returnTimeLimitDays: 14,
  requireReturnApproval: true,
  defaultRefundMethod: 'bank_transfer',

  // العمولات
  enableCommissions: true,
  commissionReferencePrefix: 'COM',
  autoGenerateCommissionReference: true,
  defaultCommissionRate: 5,
  autoCalculateOnSale: false,
  requireApprovalBeforePayment: true,

  // المسار التجاري
  enablePipeline: true,

  // عام + عرض
  defaultCurrency: getBrandDefaultCurrency(),
  defaultVatRate: getBrandVatRate() ?? SAUDI_BRAND.vatRate,
  defaultSalesTarget: 100000,
  itemsPerPage: 50,
  defaultView: 'table',
  showTaxBreakdown: true,
};

const FRENCH_PAYMENT_TERMS_ALIASES = new Set(['Net30', 'net30', 'NET30', 'Net 30']);

const FRENCH_SEGMENT_ALIASES = {
  particulier: 'retail',
  professionnel: 'wholesale',
  gros: 'wholesale',
  détail: 'retail',
  detail: 'retail',
  vip: 'vip',
  VIP: 'vip',
};

/** Map legacy nested API keys, scrub French leftovers, flatten stored config. */
export function normalizeVentesConfig(raw = {}) {
  const source = raw && typeof raw === 'object' ? { ...raw } : {};
  const merged = { ...DEFAULT_VENTES_CONFIG };

  // Flatten legacy nested sections
  if (source.orders && typeof source.orders === 'object') {
    if (source.orders.autoGenerateOrderNumber != null) {
      merged.autoGenerateOrderReference = source.orders.autoGenerateOrderNumber;
    }
    if (source.orders.requireCustomer != null) merged.requireCustomer = source.orders.requireCustomer;
    if (source.orders.allowPartialDelivery != null) merged.allowPartialDelivery = source.orders.allowPartialDelivery;
    if (source.orders.defaultStatus) merged.defaultOrderStatus = source.orders.defaultStatus;
  }
  if (source.pricing && typeof source.pricing === 'object') {
    if (source.pricing.allowStackableDiscounts != null) merged.allowStackableDiscounts = source.pricing.allowStackableDiscounts;
    if (source.pricing.defaultDiscountType) merged.defaultDiscountType = source.pricing.defaultDiscountType;
    if (source.pricing.requirePromoCodeApproval != null) merged.requirePromoCodeApproval = source.pricing.requirePromoCodeApproval;
  }
  if (source.deliveries && typeof source.deliveries === 'object') {
    if (source.deliveries.signatureRequired != null) merged.signatureRequired = source.deliveries.signatureRequired;
    if (source.deliveries.trackDeliveries != null) merged.trackDeliveries = source.deliveries.trackDeliveries;
  }
  if (source.returns && typeof source.returns === 'object') {
    if (source.returns.enableReturns != null) merged.enableReturns = source.returns.enableReturns;
    if (source.returns.returnTimeLimitDays != null) merged.returnTimeLimitDays = source.returns.returnTimeLimitDays;
    if (source.returns.requireReturnApproval != null) merged.requireReturnApproval = source.returns.requireReturnApproval;
    if (source.returns.defaultRefundMethod) merged.defaultRefundMethod = source.returns.defaultRefundMethod;
  }
  if (source.commissions && typeof source.commissions === 'object') {
    if (source.commissions.defaultCommissionRate != null) merged.defaultCommissionRate = source.commissions.defaultCommissionRate;
    if (source.commissions.autoCalculateOnSale != null) merged.autoCalculateOnSale = source.commissions.autoCalculateOnSale;
    if (source.commissions.requireApprovalBeforePayment != null) merged.requireApprovalBeforePayment = source.commissions.requireApprovalBeforePayment;
  }
  if (source.ledger && typeof source.ledger === 'object') {
    if (source.ledger.autoConvertDeliveredOrders != null) merged.autoConvertDeliveredOrders = source.ledger.autoConvertDeliveredOrders;
    if (source.ledger.defaultPaymentStatus) merged.defaultPaymentStatus = source.ledger.defaultPaymentStatus;
  }
  if (source.display && typeof source.display === 'object') {
    if (source.display.defaultView) merged.defaultView = source.display.defaultView;
    if (source.display.itemsPerPage != null) merged.itemsPerPage = source.display.itemsPerPage;
    if (source.display.showTaxBreakdown != null) merged.showTaxBreakdown = source.display.showTaxBreakdown;
  }

  // Apply flat keys from source (overrides nested)
  Object.keys(DEFAULT_VENTES_CONFIG).forEach((key) => {
    if (source[key] !== undefined && source[key] !== null) {
      merged[key] = source[key];
    }
  });

  // Legacy flat aliases
  if (source.autoGenerateOrderNumber != null && source.autoGenerateOrderReference == null) {
    merged.autoGenerateOrderReference = source.autoGenerateOrderNumber;
  }

  if (FRENCH_PAYMENT_TERMS_ALIASES.has(String(merged.defaultPaymentTerms || '').trim())) {
    merged.defaultPaymentTerms = DEFAULT_VENTES_CONFIG.defaultPaymentTerms;
  }

  merged.defaultVatRate = getBrandVatRate() ?? merged.defaultVatRate;

  delete merged.orders;
  delete merged.pricing;
  delete merged.deliveries;
  delete merged.returns;
  delete merged.commissions;
  delete merged.ledger;
  delete merged.display;
  delete merged.notifications;
  delete merged.autoGenerateOrderNumber;
  delete merged.paymentMethods;

  return merged;
}

export const VENTES_ENTITY_PREFIX_MAP = {
  priceRule: 'priceRuleReferencePrefix',
  order: 'orderReferencePrefix',
  salesOrder: 'orderReferencePrefix',
  sale: 'saleReferencePrefix',
  delivery: 'deliveryReferencePrefix',
  return: 'returnReferencePrefix',
  salesReturn: 'returnReferencePrefix',
  commission: 'commissionReferencePrefix',
};

const VENTES_AUTO_REF_MAP = {
  priceRule: 'autoGeneratePriceRuleReference',
  order: 'autoGenerateOrderReference',
  salesOrder: 'autoGenerateOrderReference',
  sale: 'autoGenerateSaleReference',
  delivery: 'autoGenerateDeliveryReference',
  return: 'autoGenerateReturnReference',
  salesReturn: 'autoGenerateReturnReference',
  commission: 'autoGenerateCommissionReference',
};

export function getVentesEntityPrefix(settings, entityKey, fallback) {
  const configKey = VENTES_ENTITY_PREFIX_MAP[entityKey];
  const fromVentes = configKey ? settings?.ventes?.[configKey] : null;
  if (fromVentes) return String(fromVentes).trim() || fallback;
  return settings?.configuration?.entityPrefixes?.[entityKey] || fallback;
}

export function isVentesAutoReferenceEnabled(settings, entityKey) {
  const key = VENTES_AUTO_REF_MAP[entityKey];
  if (!key) return true;
  const val = settings?.ventes?.[key];
  return val !== false;
}

/** Normalize legacy French segment codes to canonical keys. */
export function normalizeCustomerSegmentCode(segment) {
  const raw = String(segment || '').trim();
  if (!raw) return '';
  return FRENCH_SEGMENT_ALIASES[raw] || FRENCH_SEGMENT_ALIASES[raw.toLowerCase()] || raw;
}

export function resolveCustomerSegmentLabel(segment, options = []) {
  const normalized = normalizeCustomerSegmentCode(segment);
  if (!normalized) return '';
  const match = options.find(
    (opt) => opt.value === normalized || opt.value === segment,
  );
  return match?.label || normalized;
}

/**
 * Map ventes config prefixes into settings.configuration.entityPrefixes keys.
 * Uses salesOrder/salesReturn (not order/return) to avoid clashing with
 * facturation documentPrefixes or other modules' entity keys.
 */
export function buildVentesEntityPrefixes(config) {
  const c = normalizeVentesConfig(config);
  return {
    priceRule: c.priceRuleReferencePrefix,
    salesOrder: c.orderReferencePrefix,
    sale: c.saleReferencePrefix,
    delivery: c.deliveryReferencePrefix,
    salesReturn: c.returnReferencePrefix,
    commission: c.commissionReferencePrefix,
  };
}

/** Compute expected delivery date from order date + configured lead time. */
export function computeDefaultExpectedDeliveryDate(orderDate, leadTimeDays = 5) {
  const base = orderDate ? new Date(orderDate) : new Date();
  if (Number.isNaN(base.getTime())) return '';
  const days = Math.max(0, parseInt(leadTimeDays, 10) || 0);
  base.setDate(base.getDate() + days);
  return base.toISOString().split('T')[0];
}
